import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import BookingSelectionPage from './pages/BookingSelectionPage';
import ContactInfoPage from './pages/ContactInfoPage';
import ManageBookingPage from './pages/ManageBookingPage';
import DocUploadPage from './pages/DocUploadPage';
import { getBooking } from './api/bookingApi';
import Footer from './components/Footer';

import './App.css';

// Router wrapper to check for existing bookings
function BookingRouter() {
    const [searchParams] = useSearchParams();
    const bookingId = searchParams.get('bookingId');
    const [hasBooking, setHasBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isExpired, setIsExpired] = useState(false);

    useEffect(() => {
        const checkBooking = async () => {
            if (!bookingId) {
                setHasBooking(false);
                setLoading(false);
                return;
            }

            try {
                const response = await getBooking(bookingId);
                if (response.is_expired) {
                    setIsExpired(true);
                } else if (response.success && response.booking && response.booking.status === 'active') {
                    setHasBooking(true);
                } else {
                    setHasBooking(false);
                }
            } catch (error) {
                setHasBooking(false);
            } finally {
                setLoading(false);
            }
        };

        checkBooking();
    }, [bookingId]);

    if (loading) {
        return (
            <div className="app-container">
                <div className="loading-container" role="status" aria-label="Loading">
                    <div className="loading-spinner" aria-hidden="true"></div>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (isExpired) {
        return (
            <div className="app-container">
                <div className="error-container" role="alert" style={{ textAlign: 'center', margin: '40px auto', padding: '30px', maxWidth: '600px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 15px rgba(0,0,0,0.1)' }}>
                    <h2 style={{ color: '#d9534f', marginBottom: '15px' }}>Link Expired</h2>
                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '20px' }}>
                        This link has expired and is no longer available.<br/>
                        For help, please call us at <a href="tel:1-844-540-1882" style={{ color: '#0056b3', textDecoration: 'none', fontWeight: 'bold' }}>1-844-540-1882</a>.
                    </p>
                </div>
            </div>
        );
    }

    if (!bookingId) {
        return (
            <div className="app-container">
                <div className="error-container" role="alert" style={{ textAlign: 'center', margin: '40px auto', padding: '30px', maxWidth: '600px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 15px rgba(0,0,0,0.1)' }}>
                    <h2 style={{ color: '#d9534f', marginBottom: '15px' }}>No Valid Booking Link Found</h2>
                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '20px' }}>
                        No booking information found. Please use your personalized booking link.
                    </p>
                    <p style={{ fontSize: '1em', color: '#666' }}>
                        If you have any concerns or need assistance, please call <a href="tel:1-844-540-1882" style={{ color: '#0056b3', textDecoration: 'none', fontWeight: 'bold' }}>1-844-540-1882</a>.
                    </p>
                </div>
            </div>
        );
    }

    // If has active booking and NOT in reschedule mode, redirect to manage page
    const isRescheduling = searchParams.get('reschedule') === 'true';
    if (hasBooking && !isRescheduling) {
        return <Navigate to={`/booking?bookingId=${bookingId}`} replace />;
    }

    // Otherwise show booking selection page
    return <BookingSelectionPage />;
}

function App() {
    return (
        <BrowserRouter>
            <div className="App">
                {/* Skip navigation link — visible on keyboard focus only */}
                <a href="#main-content" className="skip-link">Skip to main content</a>

                <div className="app-header">
                    <img src="/ima-logo.png" alt="The IMA Group" className="app-logo" />
                </div>

                <main id="main-content" className="app-main-content">
                    <Routes>
                        <Route path="/book" element={<BookingRouter />} />
                        <Route path="/book/details" element={<ContactInfoPage />} />
                        <Route path="/booking" element={<ManageBookingPage />} />
                        <Route path="/docupload" element={<DocUploadPage />} />
                        <Route path="/" element={<Navigate to="/book" replace />} />
                        <Route path="*" element={<Navigate to="/book" replace />} />
                    </Routes>
                </main>

                <Footer />
            </div>
        </BrowserRouter>
    );
}

export default App;
